import { useCallback, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { uploadPdf } from '../api/imageUploadApi';
import { detectPdfLinks } from '../utils/detectPdfLinks';

export interface PdfUploadResult {
  url: string;
  hasLinks: boolean;
}

export interface PdfUploadFieldProps {
  policyUrl: string;
  onUpload: (result: PdfUploadResult) => void;
}

export function PdfUploadField({
  policyUrl,
  onUpload,
}: PdfUploadFieldProps): React.ReactElement {
  const { tokens } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const handleFileSelect = useCallback(
    async (file: File) => {
      setError(null);
      setIsUploading(true);
      try {
        // Parse links locally before upload; fall back to hasLinks=true on parse failure
        // so a malformed PDF still opens externally instead of in a broken modal.
        let hasLinks = true;
        try {
          hasLinks = await detectPdfLinks(file);
        } catch {
          hasLinks = true;
        }
        const result = await uploadPdf(file);
        setFileName(file.name);
        onUpload({ url: result.url, hasLinks });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Upload failed. Please try again.'
        );
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const displayName = fileName ?? (policyUrl ? policyUrl.split('/').pop() : null);

  const styles: Record<string, CSSProperties> = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.sm,
    },
    dropZone: {
      border: `2px dashed ${tokens.borders.default}`,
      borderRadius: tokens.radius.md,
      padding: tokens.spacing.lg,
      textAlign: 'center',
      cursor: 'pointer',
      backgroundColor: tokens.backgrounds.surface,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '14px',
      color: tokens.text.muted,
      transition: `border-color ${tokens.ui.transitionDefault}`,
    },
    currentFile: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacing.sm,
      padding: tokens.spacing.sm,
      backgroundColor: tokens.backgrounds.surface,
      borderRadius: tokens.radius.sm,
      fontFamily: tokens.typography.fontFamily,
      fontSize: '14px',
      color: tokens.text.primary,
    },
    fileIcon: {
      fontSize: '16px',
      flexShrink: 0,
    },
    fileName: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: 1,
    },
    replaceButton: {
      padding: '4px 12px',
      fontSize: '13px',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
      flexShrink: 0,
    },
    error: {
      color: tokens.ui.errorText,
      fontSize: '13px',
      fontFamily: tokens.typography.fontFamily,
    },
    uploading: {
      color: tokens.text.muted,
      fontSize: '13px',
      fontFamily: tokens.typography.fontFamily,
      fontStyle: 'italic',
    },
  };

  return (
    <div style={styles.container}>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      {displayName ? (
        <div style={styles.currentFile}>
          <span style={styles.fileIcon}>📄</span>
          <span style={styles.fileName}>{displayName}</span>
          <button
            type="button"
            style={styles.replaceButton}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            Replace
          </button>
        </div>
      ) : (
        <div
          style={styles.dropZone}
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {isUploading ? 'Uploading...' : 'Click or drag a PDF file here to upload'}
        </div>
      )}

      {isUploading && <span style={styles.uploading}>Uploading PDF...</span>}
      {error && <span style={styles.error}>{error}</span>}
    </div>
  );
}

PdfUploadField.displayName = 'PdfUploadField';
